/**
 * Locale plumbing for both web front-ends.
 *
 * The product is sold in Germany, so German stays the default and no visitor
 * has to opt into it. English is offered alongside for partners and staff who
 * do not read German — the legacy portals had no such option at all.
 *
 * The choice is kept in a cookie rather than localStorage so the Next.js
 * server can read it while rendering and stamp the right `lang` on <html>;
 * a client-only store would render German markup and then swap it after
 * hydration, which is both a flash and a hydration mismatch.
 */

export const LOCALES = ['de', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

/** German-first: the customer base and the mobile app are German. */
export const DEFAULT_LOCALE: Locale = 'de';

/** Shown in the language switcher, each in its own language. */
export const LOCALE_LABELS: Record<Locale, string> = {
  de: 'Deutsch',
  en: 'English',
};

export const LOCALE_SHORT_LABELS: Record<Locale, string> = {
  de: 'DE',
  en: 'EN',
};

/** BCP 47 tags for Intl — dates, numbers and currency follow the UI language. */
export const INTL_LOCALES: Record<Locale, string> = {
  de: 'de-DE',
  en: 'en-GB',
};

export const LOCALE_COOKIE = 'insurensa_locale';

/** A year: long enough that a returning partner keeps their choice. */
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** Narrows anything (cookie value, query param, header) to a supported locale. */
export function parseLocale(value: string | null | undefined): Locale | null {
  if (!value) return null;
  const tag = value.trim().toLowerCase().split('-')[0];
  return isLocale(tag) ? tag : null;
}

/** Reads the cookie in the browser. Returns null on the server or when unset. */
export function readLocaleCookie(): Locale | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${LOCALE_COOKIE}=([^;]*)`));
  return match ? parseLocale(decodeURIComponent(match[1] ?? '')) : null;
}

export function writeLocaleCookie(locale: Locale): void {
  if (typeof document === 'undefined') return;
  // Lax rather than Strict: following a link into the portal should not reset
  // the language. Not HttpOnly — the client is what needs to read it.
  document.cookie =
    `${LOCALE_COOKIE}=${locale}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
}

/**
 * Best guess for a first-time visitor: their own cookie, else the browser's
 * preferred languages, else German.
 */
export function detectLocale(): Locale {
  const stored = readLocaleCookie();
  if (stored) return stored;
  if (typeof navigator !== 'undefined') {
    for (const tag of navigator.languages ?? [navigator.language]) {
      const parsed = parseLocale(tag);
      if (parsed) return parsed;
    }
  }
  return DEFAULT_LOCALE;
}
